import ChecklistService from "./ChecklistService";

const checklistStore = {
  namespaced: true,
  persist: ["learnerResponse"],

  state: () => ({
    // Admin state
    checklists: [],
    currentChecklist: null,

    // Learner state
    learnerChecklist: null,
    learnerResponse: null,
    activeVersion: null,
    hasNewerVersion: false,

    // Shared
    loading: false,
    error: null,
  }),

  getters: {
    completionPercent(state) {
      return state.learnerResponse?.completionPercent || 0;
    },
    responseStatus(state) {
      return state.learnerResponse?.status || "not_started";
    },
    categoriesOrdered(state) {
      const checklist = state.learnerChecklist || state.currentChecklist;
      if (!checklist?.categories) return [];
      return [...checklist.categories].sort((a, b) => a.orderId - b.orderId);
    },
    itemsByCategory(state) {
      const checklist = state.learnerChecklist || state.currentChecklist;
      if (!checklist?.items) return {};
      const grouped = {};
      for (const item of checklist.items) {
        if (!grouped[item.category]) grouped[item.category] = [];
        grouped[item.category].push(item);
      }
      for (const cat of Object.keys(grouped)) {
        grouped[cat].sort((a, b) => a.orderId - b.orderId);
      }
      return grouped;
    },
  },

  mutations: {
    SET_CHECKLISTS(state, checklists) {
      state.checklists = checklists;
    },
    SET_CURRENT_CHECKLIST(state, checklist) {
      state.currentChecklist = checklist;
    },
    SET_LEARNER_DATA(state, { checklist, response, activeVersion, hasNewerVersion }) {
      state.learnerChecklist = checklist;
      state.learnerResponse = response;
      state.activeVersion = activeVersion;
      state.hasNewerVersion = hasNewerVersion;
    },
    SET_LEARNER_RESPONSE(state, response) {
      state.learnerResponse = response;
    },
    SET_LOADING(state, val) {
      state.loading = val;
    },
    SET_ERROR(state, err) {
      state.error = err;
    },
    HYDRATE() { /* handled by persistence plugin */ },
    RESET(state) {
      state.checklists = [];
      state.currentChecklist = null;
      state.learnerChecklist = null;
      state.learnerResponse = null;
      state.activeVersion = null;
      state.hasNewerVersion = false;
      state.loading = false;
      state.error = null;
    },
  },

  actions: {
    // ── Admin actions ──
    async fetchChecklists({ commit }, params) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const { data } = await ChecklistService.list(params);
        commit("SET_CHECKLISTS", data);
      } catch (err) {
        commit("SET_ERROR", err.response?.data?.error || err.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchChecklist({ commit }, id) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const { data } = await ChecklistService.get(id);
        commit("SET_CURRENT_CHECKLIST", data);
        return data;
      } catch (err) {
        commit("SET_ERROR", err.response?.data?.error || err.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async createChecklist({ dispatch }, payload) {
      const { data } = await ChecklistService.create(payload);
      dispatch("fetchChecklists");
      return data;
    },

    async updateChecklist({ commit }, { id, payload }) {
      const { data } = await ChecklistService.update(id, payload);
      commit("SET_CURRENT_CHECKLIST", data);
      return data;
    },

    async publishChecklist({ dispatch }, id) {
      const { data } = await ChecklistService.publish(id);
      dispatch("fetchChecklists");
      return data;
    },

    async cloneChecklist({ dispatch }, id) {
      const { data } = await ChecklistService.clone(id);
      dispatch("fetchChecklists");
      return data;
    },

    async deleteChecklist({ dispatch }, id) {
      await ChecklistService.remove(id);
      dispatch("fetchChecklists");
    },

    async uploadChecklist({ dispatch }, jsonData) {
      const { data } = await ChecklistService.upload(jsonData);
      dispatch("fetchChecklists");
      return data;
    },

    // ── Learner actions ──
    async fetchLearnerChecklist({ commit }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const { data } = await ChecklistService.getActive();
        commit("SET_LEARNER_DATA", {
          checklist: data.checklist,
          response: data.response,
          activeVersion: data.activeVersion,
          hasNewerVersion: data.hasNewerVersion,
        });
      } catch (err) {
        commit("SET_ERROR", err.response?.data?.error || err.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async saveLearnerResponse({ commit, state }, answers) {
      const checklistId =
        state.learnerResponse?.checklistId || state.learnerChecklist?._id;
      if (!checklistId) throw new Error("No checklist loaded.");

      const { data } = await ChecklistService.saveResponse({
        checklistId,
        answers,
      });
      commit("SET_LEARNER_RESPONSE", data);
      return data;
    },

    async switchToLatestVersion({ commit }) {
      const { data } = await ChecklistService.switchVersion();
      commit("SET_LEARNER_DATA", {
        checklist: data.checklist,
        response: data.response,
        activeVersion: data.checklist.version,
        hasNewerVersion: false,
      });
      return data;
    },
  },
};

export default checklistStore;
